import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FormCard, SectionHeader } from "@fawkes/ui";
import { Copy, Plus, Trash2 } from "lucide-react";

import { korteTijd, legeTijden, type Tijden, type TijdenDag, type Venster } from "../pensionreservering/tijden";
import "../pensionplanning/AccommodatieBeheer.css";
import "./PensionBrengHaaltijden.css";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// settings-pension: "Breng- en haaltijden" — per dag van de week wanneer het pension dieren aanneemt en meegeeft.
// De wizard Nieuwe reservering biedt alleen die tijden aan. Zolang een vestiging niets heeft ingericht, kiest de
// balie vrij. Wijzigen vraagt ROLE_PENSION_ADMIN, net als de api.

const DAGEN = [1, 2, 3, 4, 5, 6, 7];
const STAPPEN = [15, 30, 60];
const MAX_BLOKKEN = 3;

/** "maandag", "dinsdag", … in de taal van de gebruiker. */
function dagNaam(dag: number, locale: string): string {
  // 2026-09-14 is een maandag; zo hoeft er geen lijst met namen in de vertalingen.
  const datum = new Date(Date.UTC(2026, 8, 13 + dag));
  const naam = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(datum);
  return naam.charAt(0).toUpperCase() + naam.slice(1);
}

export function PensionBrengHaaltijden({ vestigingId, onBack }: { vestigingId: number; onBack: () => void }) {
  const t = useTranslate();
  const locale = "nl";
  const url = `pension/breng-haaltijden?vestigingId=${vestigingId}`;
  const { data, isLoading, isError, refetch } = useCustom<Tijden>({ url, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const [tijden, setTijden] = useState<Tijden>(legeTijden);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const huidig = data?.data;
  useEffect(() => {
    if (huidig && Array.isArray(huidig.dagen)) {
      setTijden({
        ...huidig,
        dagen: DAGEN.map((dag) => {
          const d = huidig.dagen.find((x) => x.dag === dag);
          const schoon = (v: Venster[] | undefined) => (v ?? []).map((x) => ({ van: korteTijd(x.van), tot: korteTijd(x.tot) }));
          return { dag, brengen: schoon(d?.brengen), halen: schoon(d?.halen) };
        }),
      });
    }
  }, [huidig]);

  const wijzig = (dag: number, soort: "brengen" | "halen", f: (v: Venster[]) => Venster[]) => {
    setOpgeslagen(false);
    setTijden((x) => ({ ...x, ingesteld: true, dagen: x.dagen.map((d) => (d.dag === dag ? { ...d, [soort]: f(d[soort]) } : d)) }));
  };
  const kopieer = (dag: number) => {
    setOpgeslagen(false);
    const bron = tijden.dagen.find((d) => d.dag === dag);
    if (!bron) return;
    setTijden((x) => ({
      ...x,
      ingesteld: true,
      dagen: x.dagen.map((d) => (d.dag === dag ? d : { ...d, brengen: [...bron.brengen], halen: [...bron.halen] })),
    }));
  };

  const compleet = (d: TijdenDag) =>
    [...d.brengen, ...d.halen].every((v) => v.van !== "" && v.tot !== "" && v.tot > v.van);
  const geldig = tijden.dagen.every(compleet);

  const bewaar = () => {
    if (!geldig) return;
    setFout(null);
    setOpgeslagen(false);
    mutate(
      {
        url,
        method: "put",
        values: {
          stapMinuten: tijden.stapMinuten,
          dagen: tijden.dagen.map((d) => ({
            dag: d.dag,
            brengen: d.brengen.filter((v) => v.van && v.tot),
            halen: d.halen.filter((v) => v.van && v.tot),
          })),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setOpgeslagen(true);
          void refetch();
        },
        onError: (e) => setFout((e as { message?: string } | null)?.message ?? t("pensionBrengHaaltijden.fout")),
      },
    );
  };

  const blokken = (d: TijdenDag, soort: "brengen" | "halen") => (
    <div className="bht-blokken">
      {d[soort].map((v, i) => (
        <div key={`${soort}-${i}`} className="bht-blok">
          <input
            type="time"
            step={tijden.stapMinuten * 60}
            aria-label={t(`pensionBrengHaaltijden.van.${soort}`, { args: [dagNaam(d.dag, locale), i + 1] })}
            value={v.van}
            disabled={!magBeheren}
            onChange={(e) => wijzig(d.dag, soort, (lijst) => lijst.map((x, j) => (j === i ? { ...x, van: e.target.value } : x)))}
          />
          <span aria-hidden="true">–</span>
          <input
            type="time"
            step={tijden.stapMinuten * 60}
            aria-label={t(`pensionBrengHaaltijden.tot.${soort}`, { args: [dagNaam(d.dag, locale), i + 1] })}
            value={v.tot}
            disabled={!magBeheren}
            onChange={(e) => wijzig(d.dag, soort, (lijst) => lijst.map((x, j) => (j === i ? { ...x, tot: e.target.value } : x)))}
          />
          {magBeheren && (
            <button
              type="button"
              className="bht-mini"
              aria-label={t(`pensionBrengHaaltijden.weg.${soort}`, { args: [dagNaam(d.dag, locale), i + 1] })}
              onClick={() => wijzig(d.dag, soort, (lijst) => lijst.filter((_, j) => j !== i))}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      {magBeheren && d[soort].length < MAX_BLOKKEN && (
        <button
          type="button"
          className="bht-toevoegen"
          aria-label={t(`pensionBrengHaaltijden.blok.${soort}`, { args: [dagNaam(d.dag, locale)] })}
          onClick={() => wijzig(d.dag, soort, (lijst) => [...lijst, { van: "10:00", tot: "16:00" }])}
        >
          <Plus size={14} aria-hidden="true" />
          {t("pensionBrengHaaltijden.blokToevoegen")}
        </button>
      )}
      {d[soort].length === 0 && <span className="bht-dicht">{t("pensionBrengHaaltijden.dicht")}</span>}
    </div>
  );

  return (
    <FormCard>
      <SectionHeader>{t("pensionBrengHaaltijden.titel")}</SectionHeader>
      <div className="acc-beheer">
        {isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : isError ? (
          <p className="fawkes-error">{t("pensionBrengHaaltijden.laden")}</p>
        ) : (
          <form
            className="acc-kaart"
            aria-label={t("pensionBrengHaaltijden.titel")}
            onSubmit={(e) => {
              e.preventDefault();
              bewaar();
            }}
          >
            <p className="acc-leeg">{t("pensionBrengHaaltijden.uitleg")}</p>
            <label className="bht-stap">
              <span>{t("pensionBrengHaaltijden.stap")}</span>
              <select
                value={tijden.stapMinuten}
                disabled={!magBeheren}
                onChange={(e) => {
                  setOpgeslagen(false);
                  setTijden((x) => ({ ...x, stapMinuten: Number(e.target.value) }));
                }}
              >
                {STAPPEN.map((s) => (
                  <option key={s} value={s}>
                    {t("pensionBrengHaaltijden.minuten", { args: [s] })}
                  </option>
                ))}
              </select>
            </label>

            <IndeelbareTabel
              tabelKey="pension-breng-haaltijden"
              columns={[
                { key: "dag", header: t("pensionBrengHaaltijden.dag"), cell: (d) => dagNaam(d.dag, locale) },
                { key: "brengen", header: t("pensionBrengHaaltijden.brengen"), cell: (d) => blokken(d, "brengen") },
                { key: "halen", header: t("pensionBrengHaaltijden.halen"), cell: (d) => blokken(d, "halen") },
                {
                  key: "kopieer", header: "", vast: true,
                  cell: (d) => (magBeheren ? (
                    <button
                      type="button"
                      className="bht-mini"
                      aria-label={t("pensionBrengHaaltijden.kopieer", { args: [dagNaam(d.dag, locale)] })}
                      title={t("pensionBrengHaaltijden.kopieer", { args: [dagNaam(d.dag, locale)] })}
                      onClick={() => kopieer(d.dag)}
                    >
                      <Copy size={14} aria-hidden="true" />
                    </button>
                  ) : null),
                },
              ]}
              rows={tijden.dagen}
              getRowKey={(d) => String(d.dag)}
              empty=""
            />

            {!geldig && <p className="acc-fout">{t("pensionBrengHaaltijden.ongeldig")}</p>}
            {!magBeheren && <p className="acc-leeg">{t("pensionBrengHaaltijden.alleenLezen")}</p>}
            {fout && (
              <p className="acc-fout" role="alert">
                {fout}
              </p>
            )}
            {opgeslagen && <p className="acc-opgeslagen">{t("pensionBrengHaaltijden.opgeslagen")}</p>}
            {magBeheren && (
              <div className="acc-voet">
                <button type="submit" className="acc-knop acc-knop--hoofd" disabled={bezig || !geldig}>
                  {t("common.save")}
                </button>
              </div>
            )}
          </form>
        )}
      </div>
      <ActionBar>
        <Button variant="secondary" onClick={onBack} disabled={bezig}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
